<template>
  <div>
    <AppNavbar />
    <section class="login-section">
      <div class="container">
        <div class="row">
          <div class="col">
            <h2 class="section-title">LOGIN</h2>
            <div class="card-box login-box">
              <!-- STEP 1: Role picker — only visible before a role is chosen -->
              <div class="form-group" v-if="!loginRole">
                <label>Login As</label>
                <div
                  class="role-select-grid"
                  role="radiogroup"
                  aria-label="Login role"
                >
                  <button
                    type="button"
                    class="role-card"
                    @click="setLoginRole('distributor')"
                  >
                    <span class="role-card-icon">
                      <i class="ri-store-2-line"></i>
                    </span>
                    <span class="role-card-text">
                      <strong>Distributor</strong>
                    </span>
                    <span class="role-card-arrow">
                      <i class="ri-arrow-right-line"></i>
                    </span>
                  </button>

                  <button
                    type="button"
                    class="role-card"
                    @click="setLoginRole('customer')"
                  >
                    <span class="role-card-icon">
                      <i class="ri-user-line"></i>
                    </span>
                    <span class="role-card-text">
                      <strong>Customer</strong>
                    </span>
                    <span class="role-card-arrow">
                      <i class="ri-arrow-right-line"></i>
                    </span>
                  </button>
                </div>
                <p class="role-hint">
                  <i class="ri-information-line"></i>
                  Select whether you're logging in as a Distributor or a
                  Customer to continue.
                </p>
              </div>

              <!-- STEP 2: Selected-role summary + form — only after a role is chosen -->
              <transition name="role-form-fade">
                <div v-if="loginRole">
                  <div class="role-selected-bar">
                    <span class="role-selected-icon">
                      <i
                        :class="
                          loginRole === 'distributor'
                            ? 'ri-store-2-line'
                            : 'ri-user-line'
                        "
                      ></i>
                    </span>
                    <span class="role-selected-text">
                      Logging in as
                      <strong>{{
                        loginRole === "distributor" ? "Distributor" : "Customer"
                      }}</strong>
                    </span>
                    <button
                      type="button"
                      class="role-change-btn"
                      :disabled="isSubmitting"
                      @click="goBackToRoleSelect"
                    >
                      <i class="ri-arrow-left-line"></i>
                      Change
                    </button>
                  </div>

                  <form @submit.prevent="handleSubmit">
                    <div class="form-group">
                      <label>Username</label>
                      <input
                        v-model.trim="username"
                        type="text"
                        :placeholder="
                          loginRole === 'distributor'
                            ? 'Enter BA ID'
                            : 'Enter email or mobile number'
                        "
                        required
                      />
                    </div>
                    <div class="form-group">
                      <label>Password</label>
                      <div class="password-wrapper">
                        <input
                          v-model="password"
                          :type="showPassword ? 'text' : 'password'"
                          placeholder="Enter password"
                          required
                        />
                        <button
                          type="button"
                          class="password-visibility-btn"
                          :aria-label="
                            showPassword ? 'Hide password' : 'Show password'
                          "
                          @click="togglePasswordVisibility"
                        >
                          <i
                            style="font-size: 20px"
                            :class="
                              showPassword ? 'ri-eye-off-line ' : 'ri-eye-line'
                            "
                          ></i>
                        </button>
                      </div>
                      <div
                        v-if="loginRole === 'distributor'"
                        class="forgot-password-row"
                      >
                        <button
                          type="button"
                          class="forgot-password-link"
                          :disabled="isSubmitting"
                          @click="openForgotPasswordModal"
                        >
                          Forgot Password?
                        </button>
                      </div>
                    </div>
                    <button
                      type="submit"
                      class="login-btn"
                      :disabled="isSubmitting"
                    >
                      {{ isSubmitting ? "SIGNING IN..." : "LOGIN" }}
                    </button>
                  </form>
                </div>
              </transition>

              <p v-if="errorMessage" class="error">
                {{ errorMessage }}
              </p>
              <p v-if="successMessage" class="success">
                {{ successMessage }}
              </p>
            </div>
          </div>
          <div class="col">
            <h2 class="section-title">NEW CUSTOMER</h2>
            <div class="card-box register-box">
              <h4>CREATE AN ACCOUNT</h4>
              <p class="register-text">
                Sign up for a free account at our store. Registration is quick
                and easy. It allows you to order from our shop.
              </p>
              <div class="hero-buttons">
                <button
                  v-if="!showAccountTypeSwitch"
                  @click="openAccountTypeSwitch"
                  class="primary-btn"
                >
                  CREATE AN ACCOUNT
                </button>
              </div>
              <transition name="role-form-fade">
                <div v-if="showAccountTypeSwitch" class="account-switch-wrap">
                  <p class="switch-label">Choose account type</p>
                  <div class="role-select-grid">
                    <button
                      type="button"
                      class="role-card"
                      @click="redirectToRegister('distributor')"
                    >
                      <span class="role-card-icon">
                        <i class="ri-store-2-line"></i>
                      </span>
                      <span class="role-card-text">
                        <strong>Distributor</strong>
                      </span>
                      <span class="role-card-arrow">
                        <i class="ri-arrow-right-line"></i>
                      </span>
                    </button>

                    <button
                      type="button"
                      class="role-card"
                      @click="redirectToRegister('customer')"
                    >
                      <span class="role-card-icon">
                        <i class="ri-user-line"></i>
                      </span>
                      <span class="role-card-text">
                        <strong>Customer</strong>
                      </span>
                      <span class="role-card-arrow">
                        <i class="ri-arrow-right-line"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="showForgotPasswordModal" class="forgot-modal-overlay">
      <div class="forgot-modal-box" @keydown.enter.prevent>
        <button
          type="button"
          class="forgot-modal-close"
          :disabled="
            forgotIsGeneratingOtp || forgotIsResendingOtp || forgotIsSubmitting
          "
          @click="closeForgotPasswordModal"
        >
          <i class="ri-close-line"></i>
        </button>

        <h3 class="forgot-modal-title">Forgot Password</h3>
        <div class="forgot-modal-subtitle-row">
          <p class="forgot-modal-subtitle">
            {{
              forgotStep === 1
                ? loginRole === "distributor"
                  ? forgotDistributorIdentityType === "email"
                    ? "Enter your email to generate OTP."
                    : "Enter your BA ID to generate OTP."
                  : "Enter your username or email to generate OTP."
                : "Enter OTP and set your new password."
            }}
          </p>
          <div
            v-if="forgotStep === 2 && forgotIdentityPreview.value"
            class="forgot-identity-inline"
          >
            <span class="otp-identity-preview">
              {{ forgotIdentityPreview.value }}
            </span>
            <button
              type="button"
              class="not-you-link"
              :disabled="forgotIsSubmitting || forgotIsResendingOtp"
              @click="goBackToForgotIdentityStep"
            >
              Not you?
            </button>
          </div>
        </div>

        <div v-if="forgotStep === 1">
          <template v-if="loginRole === 'distributor'">
            <div class="forgot-contact-grid">
              <div
                class="forgot-identity-toggle"
                role="radiogroup"
                aria-label="Forgot password identity type"
              >
                <label class="forgot-identity-option">
                  <input
                    type="radio"
                    name="forgotDistributorIdentityType"
                    value="baId"
                    v-model="forgotDistributorIdentityType"
                    :disabled="forgotIsGeneratingOtp"
                  />
                  <span>BA ID</span>
                </label>
                <label class="forgot-identity-option">
                  <input
                    type="radio"
                    name="forgotDistributorIdentityType"
                    value="email"
                    v-model="forgotDistributorIdentityType"
                    :disabled="forgotIsGeneratingOtp"
                  />
                  <span>Email</span>
                </label>
              </div>

              <div class="form-group">
                <label>{{
                  forgotDistributorIdentityType === "baId" ? "BA ID" : "Email"
                }}</label>
                <input
                  v-if="forgotDistributorIdentityType === 'baId'"
                  v-model.trim="forgotUsername"
                  type="text"
                  placeholder="Enter BA ID"
                  :disabled="forgotIsGeneratingOtp"
                />
                <input
                  v-else
                  v-model.trim="forgotDistributorEmail"
                  type="email"
                  placeholder="Enter email"
                  :disabled="forgotIsGeneratingOtp"
                />
              </div>
            </div>
          </template>

          <div v-else class="form-group">
            <label>Username</label>
            <input
              v-model.trim="forgotUsername"
              type="text"
              placeholder="Enter email or mobile number"
              :disabled="forgotIsGeneratingOtp"
            />
          </div>

          <button
            type="button"
            class="login-btn"
            :disabled="forgotIsGeneratingOtp"
            @click="handleGenerateForgotOtp"
          >
            {{ forgotIsGeneratingOtp ? "GENERATING OTP..." : "GENERATE OTP" }}
          </button>
        </div>

        <div v-else>
          <div class="form-group">
            <label>OTP</label>
            <div class="otp-action-wrap">
              <input
                ref="forgotOtpInput"
                v-model.trim="forgotOtp"
                type="text"
                name="forgotOtpCode"
                autocomplete="one-time-code"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="4"
                placeholder="Enter OTP"
                :disabled="forgotIsSubmitting"
                @input="sanitizeForgotOtpInput"
              />
              <button
                type="button"
                class="resend-otp-btn"
                :disabled="forgotIsResendingOtp || forgotIsSubmitting"
                @click="handleResendForgotOtp"
              >
                {{ forgotIsResendingOtp ? "Resending..." : "Resend OTP" }}
              </button>
            </div>
          </div>

          <div class="form-group">
            <label>New Password</label>
            <div class="password-wrapper">
              <input
                ref="forgotNewPasswordInput"
                v-model="forgotNewPassword"
                :type="showForgotNewPassword ? 'text' : 'password'"
                name="forgotNewPassword"
                autocomplete="new-password"
                placeholder="Enter new password"
                :disabled="forgotIsSubmitting"
              />
              <button
                type="button"
                class="password-visibility-btn"
                :aria-label="
                  showForgotNewPassword
                    ? 'Hide new password'
                    : 'Show new password'
                "
                :disabled="forgotIsSubmitting"
                @click="toggleForgotNewPasswordVisibility"
              >
                <i
                  style="font-size: 20px"
                  :class="
                    showForgotNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                  "
                ></i>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label>Confirm Password</label>
            <div class="password-wrapper">
              <input
                ref="forgotConfirmPasswordInput"
                v-model="forgotConfirmPassword"
                :type="showForgotConfirmPassword ? 'text' : 'password'"
                name="forgotConfirmPassword"
                autocomplete="new-password"
                placeholder="Confirm new password"
                :disabled="forgotIsSubmitting"
              />
              <button
                type="button"
                class="password-visibility-btn"
                :aria-label="
                  showForgotConfirmPassword
                    ? 'Hide confirm password'
                    : 'Show confirm password'
                "
                :disabled="forgotIsSubmitting"
                @click="toggleForgotConfirmPasswordVisibility"
              >
                <i
                  style="font-size: 20px"
                  :class="
                    showForgotConfirmPassword
                      ? 'ri-eye-off-line'
                      : 'ri-eye-line'
                  "
                ></i>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="login-btn"
            :disabled="forgotIsSubmitting"
            @click="handleConfirmForgotPassword"
          >
            {{ forgotIsSubmitting ? "CONFIRMING..." : "CONFIRM PASSWORD" }}
          </button>
        </div>

        <p v-if="forgotErrorMessage" class="error forgot-alert">
          {{ forgotErrorMessage }}
        </p>
        <p v-if="forgotWarningMessage" class="warning forgot-alert">
          {{ forgotWarningMessage }}
        </p>
        <p v-if="forgotSuccessMessage" class="success forgot-alert">
          {{ forgotSuccessMessage }}
        </p>
      </div>
    </div>

    <div v-if="showForgotResetSuccessPopup" class="forgot-modal-overlay">
      <div
        class="forgot-modal-box forgot-success-popup-box"
        @keydown.enter.prevent
      >
        <button
          type="button"
          class="forgot-modal-close"
          @click="handleForgotResetSuccessClose"
        >
          <i class="ri-close-line"></i>
        </button>

        <h3 class="forgot-modal-title">
          Your password has been successfully reset
        </h3>

        <button
          type="button"
          class="login-btn forgot-success-login-btn"
          @click="handleForgotResetSuccessClose"
        >
          Click here to login
        </button>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import AppNavbar from "./AppNavbar.vue";
import Footer from "./FooterView.vue";
import { useAuthStore } from "../stores/useAuthStore.js";
import { useAppStore } from "../stores/useAppStore.js";
import {
  generateEcomSetNewPasswordOtp as generateOtp,
  setEcomNewPassword as setPassword,
} from "rdep-ecom-sdk";

export default {
  name: "LoginForm",
  components: {
    AppNavbar,
    Footer,
  },
  data() {
    return {
      username: "",
      password: "",
      loginRole: null,
      showPassword: false,
      showAccountTypeSwitch: false,
      isSubmitting: false,
      errorMessage: "",
      successMessage: "",
      showForgotPasswordModal: false,
      forgotStep: 1,
      forgotDistributorIdentityType: "baId",
      forgotUsername: "",
      forgotDistributorEmail: "",
      forgotOtp: "",
      forgotNewPassword: "",
      forgotConfirmPassword: "",
      showForgotNewPassword: false,
      showForgotConfirmPassword: false,
      forgotTransactionId: "",
      forgotIsGeneratingOtp: false,
      forgotIsResendingOtp: false,
      forgotIsSubmitting: false,
      forgotErrorMessage: "",
      forgotWarningMessage: "",
      forgotSuccessMessage: "",
      showForgotResetSuccessPopup: false,
      forgotResetSuccessBackendMessage: "",
    };
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    appStore() {
      return useAppStore();
    },
    forgotIdentityPreview() {
      const baId = String(this.forgotUsername || "").trim();
      const email = String(this.forgotDistributorEmail || "").trim();

      if (this.loginRole === "distributor") {
        if (this.forgotDistributorIdentityType === "email") {
          if (email) return { label: "Email", value: email };
          return { label: "", value: "" };
        }
        if (baId) return { label: "BA ID", value: baId };
        return { label: "", value: "" };
      }

      if (baId) {
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(baId);
        if (isEmail) return { label: "Email", value: baId };

        const digitsOnly = baId.replace(/\D/g, "");
        if (digitsOnly.length >= 10)
          return { label: "Mobile", value: digitsOnly };

        return { label: "Username", value: baId };
      }

      return { label: "", value: "" };
    },
  },
  methods: {
    normalizeRole(role) {
      if (role === "customer" || role === "distributor") {
        return role;
      }
      return null;
    },

    // Called only from the picker (Step 1), so this always represents a
    // fresh selection — there's no "switching while the form is open"
    // path anymore, since the picker isn't rendered once a role is set.
    setLoginRole(role) {
      const normalized = this.normalizeRole(role);
      if (!normalized) return;

      this.username = "";
      this.password = "";
      this.errorMessage = "";
      this.successMessage = "";
      this.loginRole = normalized;
    },

    // The ONLY way back to the role picker. Clears the form so no
    // half-entered distributor credentials leak into a customer attempt
    // or vice versa.
    goBackToRoleSelect() {
      if (this.isSubmitting) return;

      this.loginRole = null;
      this.username = "";
      this.password = "";
      this.showPassword = false;
      this.errorMessage = "";
      this.successMessage = "";

      // Drop any ?role= query param so a page refresh doesn't silently
      // re-select a role behind the person's back.
      if (this.$route?.query?.role) {
        const query = { ...this.$route.query };
        delete query.role;
        this.$router.replace({ query });
      }
    },

    openAccountTypeSwitch() {
      this.showAccountTypeSwitch = true;
    },

    redirectToRegister(role = "customer") {
      this.$router.push({
        name: "register",
        query: {
          role,
        },
      });
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    toggleForgotNewPasswordVisibility() {
      this.showForgotNewPassword = !this.showForgotNewPassword;
    },

    toggleForgotConfirmPasswordVisibility() {
      this.showForgotConfirmPassword = !this.showForgotConfirmPassword;
    },

    getDomainNameForForgotPassword() {
      if (!this.appStore.baseUrl) {
        this.appStore.initializeDomainConfig();
      }

      return String(
        this.appStore.domainName ||
          window.location.host ||
          window.location.hostname ||
          "",
      ).trim();
    },

    normalizeApiStatusCode(response) {
      return String(response?.statusCode ?? response?.data?.statusCode ?? "");
    },

    resolveApiMessage(response) {
      const message =
        response?.statusMessage ||
        response?.message ||
        response?.data?.statusMessage ||
        response?.data?.message ||
        "";

      if (message === "OTP has been sent to registered mobile and email.") {
        return "OTP has been sent to registered email.";
      }

      return message;
    },

    resolveApiMessageRaw(response) {
      return (
        response?.statusMessage ||
        response?.message ||
        response?.data?.statusMessage ||
        response?.data?.message ||
        ""
      );
    },

    resolveApiErrorMessage(error) {
      return (
        error?.response?.data?.statusMessage ||
        error?.response?.data?.message ||
        ""
      );
    },

    resolveApiErrorStatusCode(error) {
      return String(
        error?.response?.data?.statusCode ||
          error?.response?.status ||
          error?.statusCode ||
          "",
      );
    },

    sanitizeMobileNumber(value) {
      return String(value || "").replace(/\D/g, "");
    },

    getForgotPasswordIdentity() {
      const value = String(this.forgotUsername || "").trim();
      const payload = {
        emailId: "",
        mobileNumber: "",
        distributorCode: "",
      };

      if (this.loginRole === "distributor") {
        const emailId = String(this.forgotDistributorEmail || "").trim();

        if (this.forgotDistributorIdentityType === "email") {
          if (!emailId) {
            return {
              valid: false,
              message: "Enter email.",
              payload: null,
            };
          }
          payload.emailId = emailId;
          return { valid: true, message: "", payload };
        }

        if (!value) {
          return {
            valid: false,
            message: "Enter BA ID.",
            payload: null,
          };
        }

        payload.distributorCode = value;
        return { valid: true, message: "", payload };
      }

      if (!value) {
        return {
          valid: false,
          message: "Username is required.",
          payload: null,
        };
      }

      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (isEmail) {
        payload.emailId = value;
        return { valid: true, message: "", payload };
      }

      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length >= 10) {
        payload.mobileNumber = digitsOnly;
        return { valid: true, message: "", payload };
      }

      return {
        valid: false,
        message: "Please enter a valid email or mobile number.",
        payload: null,
      };
    },

    async runForgotPasswordRequest(requester) {
      try {
        const response = await requester();
        const statusCode = this.normalizeApiStatusCode(response);
        const message = this.resolveApiMessage(response);

        if (!statusCode || statusCode === "200") {
          return {
            ok: true,
            response,
            message,
          };
        }

        return {
          ok: false,
          response,
          message,
          statusCode,
        };
      } catch (error) {
        return {
          ok: false,
          response: null,
          message: this.resolveApiErrorMessage(error),
          statusCode: this.resolveApiErrorStatusCode(error),
        };
      }
    },

    resetForgotPasswordState() {
      this.forgotStep = 1;
      this.forgotDistributorIdentityType = "baId";
      this.forgotUsername = "";
      this.forgotDistributorEmail = "";
      this.forgotOtp = "";
      this.forgotNewPassword = "";
      this.forgotConfirmPassword = "";
      this.showForgotNewPassword = false;
      this.showForgotConfirmPassword = false;
      this.forgotTransactionId = "";
      this.forgotIsGeneratingOtp = false;
      this.forgotIsResendingOtp = false;
      this.forgotIsSubmitting = false;
      this.forgotErrorMessage = "";
      this.forgotWarningMessage = "";
      this.forgotSuccessMessage = "";
    },

    openForgotPasswordModal() {
      if (!this.loginRole) {
        this.errorMessage =
          "Please select whether you're logging in as a Distributor or Customer first.";
        return;
      }

      if (this.loginRole === "customer") {
        this.errorMessage =
          "Forgot password is currently available only for Distributor.";
        return;
      }

      this.errorMessage = "";
      this.successMessage = "";
      this.showForgotResetSuccessPopup = false;
      this.forgotResetSuccessBackendMessage = "";
      this.resetForgotPasswordState();
      this.forgotUsername = "";
      this.forgotDistributorEmail = "";
      this.showForgotPasswordModal = true;
    },

    closeForgotPasswordModal() {
      if (
        this.forgotIsGeneratingOtp ||
        this.forgotIsResendingOtp ||
        this.forgotIsSubmitting
      ) {
        return;
      }

      this.showForgotPasswordModal = false;
      this.resetForgotPasswordState();
    },

    goBackToForgotIdentityStep() {
      if (this.forgotIsSubmitting || this.forgotIsResendingOtp) {
        return;
      }

      this.forgotStep = 1;
      this.forgotOtp = "";
      this.forgotNewPassword = "";
      this.forgotConfirmPassword = "";
      this.showForgotNewPassword = false;
      this.showForgotConfirmPassword = false;
      this.forgotErrorMessage = "";
      this.forgotWarningMessage = "";
      this.forgotSuccessMessage = "";
    },

    clearForgotStepTwoFields() {
      this.forgotOtp = "";
      this.forgotNewPassword = "";
      this.forgotConfirmPassword = "";

      this.$nextTick(() => {
        const otpInput = this.$refs.forgotOtpInput;
        const newPasswordInput = this.$refs.forgotNewPasswordInput;
        const confirmPasswordInput = this.$refs.forgotConfirmPasswordInput;

        if (otpInput) otpInput.value = "";
        if (newPasswordInput) newPasswordInput.value = "";
        if (confirmPasswordInput) confirmPasswordInput.value = "";

        // Some password managers inject values after render; clear once more.
        window.setTimeout(() => {
          if (otpInput) otpInput.value = "";
          if (newPasswordInput) newPasswordInput.value = "";
          if (confirmPasswordInput) confirmPasswordInput.value = "";
          this.forgotOtp = "";
          this.forgotNewPassword = "";
          this.forgotConfirmPassword = "";
        }, 120);
      });
    },

    sanitizeForgotOtpInput() {
      this.forgotOtp = String(this.forgotOtp || "")
        .replace(/\D/g, "")
        .slice(0, 4);
    },

    clearLoginForm() {
      this.username = "";
      this.password = "";
      this.showPassword = false;
      this.errorMessage = "";
      this.successMessage = "";
      this.loginRole = null;
    },

    async handleForgotResetSuccessClose() {
      this.showForgotResetSuccessPopup = false;
      this.forgotResetSuccessBackendMessage = "";
      this.resetForgotPasswordState();
      this.showForgotPasswordModal = false;
      this.clearLoginForm();

      try {
        if (this.$route?.name !== "login") {
          await this.$router.push({ name: "login" });
          return;
        }

        if (this.$route?.query?.role) {
          const query = { ...this.$route.query };
          delete query.role;
          await this.$router.replace({ query });
        }
      } catch (error) {
        try {
          await this.$router.push("/login");
        } catch (nestedError) {
          // no-op: if already on login route, keep current state
        }
      }
    },

    async handleGenerateForgotOtp() {
      this.forgotErrorMessage = "";
      this.forgotWarningMessage = "";
      this.forgotSuccessMessage = "";

      const identity = this.getForgotPasswordIdentity();
      if (!identity.valid) {
        this.forgotErrorMessage = identity.message;
        return;
      }

      const domainName = this.getDomainNameForForgotPassword();
      if (!domainName) {
        this.forgotErrorMessage =
          "Domain name is required for forgot password.";
        return;
      }

      this.forgotIsGeneratingOtp = true;

      try {
        const result = await this.runForgotPasswordRequest(() =>
          generateOtp({
            ...identity.payload,
            domainName,
          }),
        );

        if (!result.ok) {
          this.forgotErrorMessage = result.message;
          return;
        }

        const response = result.response;
        const message = result.message;

        this.forgotTransactionId = String(
          response?.transactionId || response?.data?.transactionId || "",
        ).trim();
        this.forgotStep = 2;
        this.clearForgotStepTwoFields();
        this.forgotSuccessMessage = message || "OTP generated successfully.";
      } catch (error) {
        this.forgotErrorMessage = this.resolveApiErrorMessage(error);
      } finally {
        this.forgotIsGeneratingOtp = false;
      }
    },

    async handleResendForgotOtp() {
      this.forgotErrorMessage = "";
      this.forgotWarningMessage = "";
      this.forgotSuccessMessage = "";

      const identity = this.getForgotPasswordIdentity();
      if (!identity.valid) {
        this.forgotErrorMessage = identity.message;
        return;
      }

      const domainName = this.getDomainNameForForgotPassword();
      if (!domainName) {
        this.forgotErrorMessage =
          "Domain name is required for forgot password.";
        return;
      }

      this.forgotIsResendingOtp = true;

      try {
        const result = await this.runForgotPasswordRequest(() =>
          generateOtp({
            ...identity.payload,
            domainName,
          }),
        );

        if (!result.ok) {
          this.forgotErrorMessage = result.message;
          return;
        }

        const response = result.response;
        const message = result.message;

        this.forgotTransactionId = String(
          response?.transactionId || response?.data?.transactionId || "",
        ).trim();
        this.forgotSuccessMessage = message || "OTP resent successfully.";
      } catch (error) {
        this.forgotErrorMessage = this.resolveApiErrorMessage(error);
      } finally {
        this.forgotIsResendingOtp = false;
      }
    },

    async handleConfirmForgotPassword() {
      this.forgotErrorMessage = "";
      this.forgotWarningMessage = "";
      this.forgotSuccessMessage = "";

      const identity = this.getForgotPasswordIdentity();
      const otp = String(this.forgotOtp || "").trim();
      const newPassword = String(this.forgotNewPassword || "");
      const confirmPassword = String(this.forgotConfirmPassword || "");

      if (!identity.valid) {
        this.forgotErrorMessage = identity.message;
        return;
      }

      const domainName = this.getDomainNameForForgotPassword();
      if (!domainName) {
        this.forgotErrorMessage =
          "Domain name is required for forgot password.";
        return;
      }
      if (!otp) {
        this.forgotErrorMessage = "OTP is required.";
        return;
      }
      if (!newPassword || !confirmPassword) {
        this.forgotErrorMessage = "Please enter and confirm your new password.";
        return;
      }
      if (newPassword.length < 6) {
        this.forgotErrorMessage = "New password must be at least 6 characters.";
        return;
      }
      if (newPassword !== confirmPassword) {
        this.forgotErrorMessage = "Passwords do not match.";
        return;
      }

      this.forgotIsSubmitting = true;

      try {
        const result = await this.runForgotPasswordRequest(() =>
          setPassword({
            ...identity.payload,
            transactionId: this.forgotTransactionId || undefined,
            otp,
            newPassword,
            domainName,
          }),
        );

        if (!result.ok) {
          this.forgotErrorMessage = result.message;
          return;
        }

        const rawBackendMessage = this.resolveApiMessageRaw(result.response);

        this.password = "";
        this.showForgotPasswordModal = false;
        this.resetForgotPasswordState();
        this.showForgotResetSuccessPopup = true;
        this.forgotResetSuccessBackendMessage =
          rawBackendMessage || "Password reset successful.";
        this.successMessage = "";
        this.errorMessage = "";
      } catch (error) {
        this.forgotErrorMessage = this.resolveApiErrorMessage(error);
      } finally {
        this.forgotIsSubmitting = false;
      }
    },

    async handleSubmit() {
      this.errorMessage = "";
      this.successMessage = "";

      const selectedRole = this.normalizeRole(this.loginRole);
      if (!selectedRole) {
        this.errorMessage =
          "Please select whether you're logging in as a Distributor or Customer.";
        return;
      }

      this.isSubmitting = true;

      try {
        const result = await this.authStore.LogIn({
          username: this.username,
          password: this.password,
          role: selectedRole,
          loginRole: selectedRole,
        });

        this.successMessage = result?.statusMessage || "Login successful";

        const redirectQuery = this.$route?.query?.redirect;
        const redirectTarget = Array.isArray(redirectQuery)
          ? String(redirectQuery[0] || "").trim()
          : String(redirectQuery || "").trim();

        // Only allow internal app redirects.
        if (redirectTarget.startsWith("/")) {
          await this.$router.push(redirectTarget);
        } else {
          await this.$router.push({ name: "home" });
        }
      } catch (error) {
        this.errorMessage = error?.message || "Login failed";
      } finally {
        this.isSubmitting = false;
      }
    },

    async handleLogout() {
      this.errorMessage = "";
      this.successMessage = "";

      const response = await this.authStore.logOut();
      this.successMessage = response?.statusMessage || "Logged out";
    },
  },
  created() {
    // Only pre-select a role if a valid one was explicitly passed via the
    // route query. Otherwise the picker starts unselected and the form
    // stays hidden until the person chooses.
    this.loginRole = this.normalizeRole(this.$route?.query?.role);
  },
};
</script>

<style scoped>
.login-section {
  /* IndiKonnect Unified Brand Tokens */
  --cobalt: #003da5;
  --gold: #ffc72c;
  --charcoal: #333f48;
  --dark-blue: #0a2240;
  --pure-white: #ffffff;
  --light-gray-bg: #f9fafc;
  --soft-border: rgba(0, 61, 165, 0.08);
  padding: 20px 0;
  background: linear-gradient(
    180deg,
    var(--pure-white) 0%,
    var(--light-gray-bg) 100%
  );
  color: var(--charcoal);
  width: 100%;
  box-sizing: border-box;
}

.container {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}

.row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 48px;
  align-items: stretch;
}

.section-title {
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: var(--cobalt); /* Anchored with Signature Cobalt Blue */
  margin-bottom: 28px;
  text-transform: uppercase;
}

.card-box {
  background: var(--pure-white);
  border: 1px solid var(--soft-border);
  border-radius: 20px;
  padding: 48px;
  min-height: 400px;
  box-sizing: border-box;
  box-shadow: 0 10px 30px rgba(10, 34, 64, 0.02);
  position: relative;
}

/* Precise Top Identity Accents (No hover transitions to avoid element shifting) */
.card-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  border-radius: 20px 20px 0 0;
}

.login-box::before {
  background-color: var(--cobalt);
}

.register-box::before {
  background-color: var(--gold);
}

.form-group {
  margin-bottom: 14px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--dark-blue);
  letter-spacing: 0.2px;
}

input {
  width: 100%;
  height: 54px;
  padding: 0 20px;
  font-size: 15px;
  color: var(--charcoal);
  border: 1px solid rgba(10, 34, 64, 0.12);
  border-radius: 12px;
  background: var(--light-gray-bg);
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

input:focus {
  background: var(--pure-white);
  border-color: var(--cobalt);
  box-shadow: 0 0 0 4px rgba(0, 61, 165, 0.08);
}

.password-wrapper {
  position: relative;
}

.forgot-password-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.forgot-password-link {
  border: none;
  background: transparent;
  color: var(--cobalt);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.forgot-password-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.forgot-password-link:hover:not(:disabled) {
  color: #002f7e;
}

.password-visibility-btn {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  line-height: 1;
}

.password-wrapper i {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #8a94a6;
  cursor: pointer;
  font-size: 16px;
  transition: color 0.2s ease;
}

.password-wrapper i:hover {
  color: var(--cobalt);
}

.login-btn {
  width: 100%;
  height: 54px;
  margin-top: 12px;
  background: linear-gradient(135deg, var(--gold) 0%, #f5b000 100%);
  color: var(--dark-blue);
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(255, 199, 44, 0.2);
  transition: all 0.3s ease;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(255, 199, 44, 0.28);
}

.login-btn:disabled {
  opacity: 0.6;
  background: #e2e8f0;
  color: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
}

.card-box h4 {
  font-size: 22px;
  font-weight: 700;
  color: var(--dark-blue);
  margin-bottom: 16px;
  margin-top: 4px;
}

.register-text {
  margin: 0 0 32px;
  color: var(--charcoal);
  opacity: 0.85;
  font-size: 15px;
  line-height: 1.65;
}

.hero-buttons {
  display: flex;
  justify-content: flex-start;
}

.primary-btn {
  background: linear-gradient(135deg, var(--gold) 0%, #f5b000 100%);
  color: var(--dark-blue);
  border: none;
  border-radius: 50px;
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(255, 199, 44, 0.2);
  transition: all 0.3s ease;
}

.primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(255, 199, 44, 0.28);
}

/* ROLE / ACCOUNT TYPE SELECTOR — card style */
.role-select-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.role-card {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 16px;
  border: 1.5px solid rgba(10, 34, 64, 0.12);
  border-radius: 14px;
  background: var(--light-gray-bg);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  position: relative;
}

.role-card:hover {
  border-color: rgba(0, 61, 165, 0.35);
  background: var(--pure-white);
  transform: translateY(-1px);
}

.role-card-icon {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 61, 165, 0.08);
  color: var(--cobalt);
  font-size: 20px;
  transition: all 0.2s ease;
}

.role-card-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.role-card-text strong {
  font-size: 14.5px;
  font-weight: 800;
  color: var(--dark-blue);
}

.role-card-text small {
  font-size: 11.5px;
  color: #7c8797;
  line-height: 1.4;
}

.role-card-arrow {
  margin-left: auto;
  flex-shrink: 0;
  color: #b7c1d1;
  font-size: 18px;
  transition: all 0.2s ease;
}

.role-card:hover .role-card-arrow {
  color: var(--cobalt);
  transform: translateX(2px);
}

.role-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 0;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(0, 61, 165, 0.04);
  border: 1px dashed rgba(0, 61, 165, 0.2);
  color: #5a6b82;
  font-size: 13px;
  font-weight: 500;
}

.role-hint i {
  color: var(--cobalt);
  font-size: 16px;
  flex-shrink: 0;
}

/* Selected-role summary bar shown above the form (Step 2) */
.role-selected-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 12px;
  background: rgba(0, 61, 165, 0.05);
  border: 1px solid rgba(0, 61, 165, 0.14);
}

.role-selected-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cobalt);
  color: var(--pure-white);
  font-size: 16px;
}

.role-selected-text {
  flex: 1;
  font-size: 14px;
  color: var(--charcoal);
}

.role-selected-text strong {
  color: var(--dark-blue);
}

.role-change-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--pure-white);
  border: 1.5px solid rgba(0, 61, 165, 0.25);
  color: var(--cobalt);
  border-radius: 50px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.role-change-btn:hover:not(:disabled) {
  background: rgba(0, 61, 165, 0.06);
  border-color: var(--cobalt);
}

.role-change-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.role-form-fade-enter-active,
.role-form-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.role-form-fade-enter-from,
.role-form-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.account-switch-wrap {
  margin-top: 20px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid rgba(0, 61, 165, 0.16);
  background: rgba(0, 61, 165, 0.03);
}

.switch-label {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  color: var(--dark-blue);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.forgot-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 34, 64, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  z-index: 2000;
}

.forgot-modal-box {
  width: 100%;
  max-width: 520px;
  background: var(--pure-white);
  border-radius: 18px;
  border: 1px solid rgba(0, 61, 165, 0.14);
  box-shadow: 0 20px 48px rgba(10, 34, 64, 0.24);
  padding: 26px;
  position: relative;
}

.forgot-success-popup-box {
  max-width: 500px;
}

.forgot-success-popup-subtitle {
  margin-top: 12px;
  white-space: pre-wrap;
}

.forgot-success-login-btn {
  margin-top: 24px;
}

.forgot-modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(10, 34, 64, 0.15);
  background: #f8fafc;
  color: var(--dark-blue);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.forgot-modal-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.forgot-modal-title {
  margin: 0;
  font-size: 22px;
  color: var(--dark-blue);
  font-weight: 600;
}

.forgot-modal-subtitle {
  margin: 8px 0 0;
  color: #607086;
  font-size: 14px;
}

.forgot-modal-subtitle-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.otp-action-wrap {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}

.otp-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.forgot-identity-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.otp-identity-preview {
  color: #5a6b82;
  font-size: 12px;
  font-weight: 600;
  font-style: italic;
}

.not-you-link {
  border: none;
  background: transparent;
  color: var(--cobalt);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.not-you-link:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.forgot-contact-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.forgot-identity-toggle {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 4px;
}

.forgot-identity-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--dark-blue);
  cursor: pointer;
}

.forgot-identity-option input {
  width: auto;
  height: auto;
  margin: 0;
  accent-color: var(--cobalt);
}

.resend-otp-btn {
  height: 54px;
  border: 1px solid rgba(0, 61, 165, 0.25);
  background: rgba(0, 61, 165, 0.06);
  color: var(--cobalt);
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  padding: 0 14px;
  cursor: pointer;
  font-family: inherit;
}

.resend-otp-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.forgot-alert {
  margin-top: 14px;
}

.warning {
  color: #b45309;
  background-color: #fffbeb;
  border-left: 3px solid #d97706;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 20px;
  font-size: 14px;
  font-weight: 500;
}

.error {
  color: #dc2626;
  background-color: #fef2f2;
  border-left: 3px solid #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 20px;
  font-size: 14px;
  font-weight: 500;
}

.success {
  color: #16a34a;
  background-color: #f0fdf4;
  border-left: 3px solid #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 20px;
  font-size: 14px;
  font-weight: 500;
}

/* Responsive Rules */
@media (max-width: 991px) {
  .login-section {
    padding: 80px 0;
  }

  .row {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .section-title {
    font-size: 28px;
    margin-bottom: 20px;
  }

  .card-box {
    min-height: auto;
    padding: 36px 24px;
  }
}

@media (max-width: 640px) {
  .role-select-grid {
    grid-template-columns: 1fr;
  }

  .forgot-contact-grid {
    grid-template-columns: 1fr;
  }

  .role-card-arrow {
    display: none;
  }
}

@media (max-width: 480px) {
  .login-section {
    padding: 60px 0;
  }
  .container {
    padding: 0 16px;
  }

  .section-title {
    font-size: 24px;
  }

  .card-box {
    padding: 28px 20px;
    border-radius: 16px;
  }
  .primary-btn,
  .login-btn {
    width: 100%;
    text-align: center;
  }

  .forgot-modal-box {
    padding: 20px 16px;
  }

  .otp-action-wrap {
    grid-template-columns: 1fr;
  }
}
</style>
